import { useState } from "react";
import { Download, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Btn } from "../../components/Btn";
import { Modal } from "../../components/Modal";
import { type LogEvent } from "../../data";
import { dotColor } from "./SummaryPage";
import { Kv } from "../../components/dashboardModals";
import { listEvents } from "../../api/endpoints";
import { toLogEvent } from "../../api/adapters";
import { useApiFallback } from "../../hooks/useApiFallback";
import { useToast } from "../../components/Toast";
import { downloadText, toCsv } from "../../utils/download";

type Filt = "all" | "publish" | "revoke" | "sync" | "cert" | "user" | "group" | "warn";
const PAGE_SIZE = 50;

export default function GlobalLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filt, setFilt] = useState<Filt>("all");
  const [q, setQ] = useState(() => searchParams.get("q") ?? "");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<LogEvent | null>(null);
  const push = useToast();

  const typeParam = filt === "all" ? undefined : filt;

  const eventsQ = useQuery({
    queryKey: ["events", { q, type: typeParam, page }],
    queryFn: () => listEvents({ q: q || undefined, type: typeParam, page, page_size: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const apiEvents = eventsQ.data?.items.map(toLogEvent);
  // Fallback на пустой массив — иначе на reload мелькает демо-журнал.
  const events = useApiFallback<LogEvent[]>(apiEvents, eventsQ.isError, []);

  const total = eventsQ.data?.meta.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const loading = eventsQ.isLoading || eventsQ.isFetching;

  function onChangeFilt(v: Filt) {
    setFilt(v);
    setPage(1);
  }
  function onChangeQ(v: string) {
    setQ(v);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (v) next.set("q", v);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  }

  async function exportCsv() {
    try {
      const response = await listEvents({
        q: q || undefined,
        type: typeParam,
        page: 1,
        page_size: 200,
      });
      const rows = response.items.map(toLogEvent);
      downloadText(
        `astramarket-events-${new Date().toISOString().slice(0, 10)}.csv`,
        "\ufeff" + toCsv([
          ["ID", "Время", "Пользователь", "Событие", "Тип", "Источник", "Объект", "Детали"],
          ...rows.map((event) => [
            event.id,
            event.time,
            event.who,
            event.text,
            event.type,
            event.source,
            event.target,
            event.detail,
          ]),
        ]),
        "text/csv;charset=utf-8",
      );
      push({ kind: "success", text: `Экспортировано событий: ${rows.length}` });
    } catch {
      push({ kind: "warn", text: "Не удалось выгрузить журнал" });
    }
  }

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Журнал событий
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            Сводный журнал по всему контуру. Подписан и хранится в Глобале.
          </div>
        </div>
        <Btn icon={Download} onClick={exportCsv}>Экспорт CSV</Btn>
      </header>

      <div className="relative">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-fg-faint)]" />
        <input
          value={q}
          onChange={(e) => onChangeQ(e.target.value)}
          placeholder="Поиск по логину, объекту, ID события (evt-…)"
          className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] py-2.5 pl-9 pr-3 text-[13px] text-[var(--color-fg)] outline-none placeholder:text-[var(--color-fg-faint)] focus:border-[var(--color-fg-muted)]"
        />
      </div>

      <div className="-mx-1 mt-3 overflow-x-auto">
        <div className="flex min-w-max gap-1 px-1">
          <Pill v="all"     f={filt} on={onChangeFilt}>все события</Pill>
          <Pill v="publish" f={filt} on={onChangeFilt}>публикация</Pill>
          <Pill v="revoke"  f={filt} on={onChangeFilt}>отзыв</Pill>
          <Pill v="sync"    f={filt} on={onChangeFilt}>синхронизация</Pill>
          <Pill v="cert"    f={filt} on={onChangeFilt}>сертификаты</Pill>
          <Pill v="user"    f={filt} on={onChangeFilt}>пользователи</Pill>
          <Pill v="group"   f={filt} on={onChangeFilt}>группы</Pill>
          <Pill v="warn"    f={filt} on={onChangeFilt}>предупреждения</Pill>
        </div>
      </div>

      <section className={"mt-3 overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] " + (loading ? "opacity-70 transition-opacity" : "transition-opacity")}>
        <div className="flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2 text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
          <span>journal · {events.value.length} записей · всего {total}</span>
          <span>signed by global</span>
        </div>
        <ul className="divide-y divide-[var(--color-border)]">
          {events.value.map((e, i) => (
            <li
              key={e.id}
              style={{ animationDelay: `${Math.min(i, 10) * 25}ms` }}
              className="stagger-up transition-colors hover:bg-[var(--color-surface-2)]"
            >
              <button onClick={() => setOpen(e)} className="grid w-full grid-cols-[64px_18px_1fr_auto] items-start gap-3 px-3 py-2.5 text-left text-[12.5px]">
                <span className="font-mono text-[11px] text-[var(--color-fg-muted)]">{e.time}</span>
                <span className="mt-1 inline-block h-2 w-2 rounded-full" style={{ background: dotColor(e.color) }} />
                <div className="min-w-0">
                  <div className="truncate text-[var(--color-fg)]">
                    <span className="font-mono font-medium">{e.who}</span>{" "}
                    <span className="text-[var(--color-fg-muted)]">{e.text}</span>
                  </div>
                  <div className="mt-0.5 truncate font-mono text-[10.5px] text-[var(--color-fg-faint)]">
                    {e.id} · {e.target}
                  </div>
                </div>
                <span className="shrink-0 self-center text-[11px] text-[var(--color-fg-faint)]">{e.when}</span>
              </button>
            </li>
          ))}
          {!events.value.length && (
            <li className="px-3 py-10 text-center text-[12.5px] text-[var(--color-fg-muted)]">
              Нет событий по этому фильтру.
            </li>
          )}
        </ul>
      </section>

      {pageCount > 1 && (
        <div className="mt-3 flex items-center justify-end gap-1 text-[12px] text-[var(--color-fg-muted)]">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="grid h-7 w-7 place-items-center rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] disabled:opacity-40 hover:bg-[var(--color-surface-2)]"
            aria-label="назад"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="px-2 font-mono">{page} / {pageCount}</span>
          <button
            onClick={() => setPage(Math.min(pageCount, page + 1))}
            disabled={page >= pageCount}
            className="grid h-7 w-7 place-items-center rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] disabled:opacity-40 hover:bg-[var(--color-surface-2)]"
            aria-label="вперёд"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      <Modal
        open={!!open}
        onClose={() => setOpen(null)}
        title={open && (
          <span className="flex items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: dotColor(open.color) }} />
            Событие журнала
          </span>
        )}
        subtitle={open && <span className="font-mono">{open.id} · {open.time}</span>}
        footer={
          <>
            <button onClick={() => setOpen(null)} className="rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--color-fg)] hover:bg-[var(--color-surface-2)]">
              Закрыть
            </button>
          </>
        }
      >
        {open && (
          <div className="space-y-4 text-[12.5px]">
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
              <div className="text-[var(--color-fg)]">
                <span className="font-mono font-medium">{open.who}</span>{" "}
                <span className="text-[var(--color-fg-muted)]">{open.text}</span>
              </div>
            </div>
            <p className="text-[var(--color-fg-muted)]">{open.detail}</p>
            <div className="grid grid-cols-1 gap-x-6 gap-y-3 pt-2 sm:grid-cols-2">
              <Kv label="Тип" mono value={open.type} />
              <Kv label="Источник" mono value={open.source} />
              <Kv label="Объект" mono value={open.target} />
              <Kv label="Время" mono value={open.time} />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

function Pill({ v, f, on, children }: { v: Filt; f: Filt; on: (v: Filt) => void; children: React.ReactNode }) {
  const active = f === v;
  return (
    <button
      onClick={() => on(v)}
      className={
        "rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors " +
        (active
          ? "bg-[var(--color-brand)] text-white"
          : "bg-[var(--color-surface-2)] text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-fg)]")
      }
    >
      {children}
    </button>
  );
}
